import { useMutationAbstract } from "../hooks/useMutationFormAbstract";
import axios from "../utils/axios";

type TxnRespType = {
  RRN: string;
  ProcessedDateTime: string;
  ResponseText: string;
  ResponseCode: string;
  TxnNumber: string;
  BankResponseCode: string;
};

type APIResponseType = {
  ResponseCode: number;
  ResponseText: string;
};

export const useSubmitResultKey = () =>
  useMutationAbstract<
    {
      APIResponse: APIResponseType;
      TxnResp: TxnRespType;
    },
    {
      APIResponse: APIResponseType;
      TxnResp: null;
    },
    {
      ResultKey: string;
      "g-recaptcha-response": string;
      transaction_for: string;
      transaction_uuid: string;
      merchant_reference?: string;
      order_uuid?: string;
    }
  >({
    mutationFn: async (data) => {
      const cacheKey = `payment_result_${data.ResultKey}`;

      // Use localStorage (not sessionStorage) so the result survives tab closes
      // and browser restarts. The ResultKey expires in ~10 min on BPoint's side,
      // so without this a customer opening the same URL in a new tab would trigger
      // a re-call against an expired ResultKey and get "Session not found".
      const cachedResponse = localStorage.getItem(cacheKey);
      if (cachedResponse) {
        try {
          return JSON.parse(cachedResponse);
        } catch (error) {
          // If parsing fails, continue with API call
          console.error("Failed to parse cached payment result:", error);
        }
      }

      // Make API call if no cache exists
      const response = await axios.post(
        "/api/payment-gateway-bpoint/submit-result-key",
        data
      );
      const responseData = response.data;

      // Cache the successful response
      localStorage.setItem(cacheKey, JSON.stringify(responseData));

      return responseData;
    },
  });

export const useSubmitResultKeySawfish = () =>
  useMutationAbstract<{
    "g-recaptcha-response": string;
    transaction_for: string;
    transaction_uuid: string;
    sawfish_transaction_uuid: string;
    merchant_reference?: string;
    order_uuid?: string;
    name?: string;
    email?: string;
    totalQuoteAmount?: string;
    amount?: number;
  }>({
    mutationFn: async (data) => {
      const cacheKey = `sawfish_payment_result_${data.transaction_uuid}`;

      // Check sessionStorage for cached response
      const cachedResponse = sessionStorage.getItem(cacheKey);
      if (cachedResponse) {
        try {
          return JSON.parse(cachedResponse);
        } catch (error) {
          // If parsing fails, continue with API call
          console.error("Failed to parse cached payment result:", error);
        }
      }

      // Make API call if no cache exists
      const response = await axios.post(
        "/api/payment-gateway-sawfish/submit-result-key",
        data
      );
      const responseData = response.data;

      // Cache the successful response
      sessionStorage.setItem(cacheKey, JSON.stringify(responseData));

      return responseData;
    },
  });

export const useAcceptOrderFromPreviousOrder = () =>
  useMutationAbstract({
    mutationFn: (data) =>
      axios
        .post("/api/previous-order-update-accept", data)
        .then((res) => res.data),
  });
